<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useInitResetPassword } from '@/mutations/auth.mutations';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const { initReset, isPending } = useInitResetPassword();

const email = ref('');
const isSent = ref(false);
const error = ref('');

async function handleSubmit() {
  if (!email.value.trim()) return;
  error.value = '';
  try {
    await initReset({ email: email.value });
    sessionStorage.setItem('reset_email', email.value);
    isSent.value = true;
  } catch (err: any) {
    const msg = err?.response?.data?.message?.[0];
    error.value = msg || t('auth.forgot.errorDefault');
  }
}
</script>

<template>
  <div class="fp">
    <div class="fp__icon-wrap">
      <i class="pi pi-key fp__icon" />
    </div>

    <h2 class="fp__title">{{ t('auth.forgot.title') }}</h2>
    <p class="fp__subtitle">{{ t('auth.forgot.subtitle') }}</p>

    <div v-if="isSent" class="fp__success">
      <i class="pi pi-check-circle fp__success-icon" />
      <span>{{ t('auth.forgot.successMsg', { email }) }}</span>
    </div>

    <form v-else class="fp__form" @submit.prevent="handleSubmit">
      <div v-if="error" class="fp__error">
        <i class="pi pi-exclamation-circle" /> {{ error }}
      </div>

      <div class="fp__field">
        <label class="fp__label" for="fp-email">{{
          t('auth.forgot.emailLabel')
        }}</label>
        <input
          id="fp-email"
          class="fp__input"
          type="email"
          :placeholder="t('auth.forgot.emailPlaceholder')"
          required
          v-model="email"
        />
      </div>

      <button type="submit" class="fp__btn" :disabled="isPending">
        <span v-if="isPending" class="fp__spinner" />
        <span v-else>{{ t('auth.forgot.submit') }}</span>
      </button>
    </form>

    <button
      v-if="isSent"
      class="fp__btn"
      style="margin-top: 0.25rem"
      @click="router.push('/otp')"
    >
      {{ t('auth.forgot.nextBtn') }}
    </button>

    <RouterLink to="/login" class="fp__back">{{
      t('auth.forgot.back')
    }}</RouterLink>
  </div>
</template>

<style scoped>
.fp {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.125rem;
  text-align: center;
  width: 100%;
}

.fp__icon-wrap {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 1.25rem;
  background: rgba(255, 214, 10, 0.15);
  border: 1px solid rgba(255, 214, 10, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.fp__icon {
  font-size: 2rem;
  color: #c4a000;
}
.fp__title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
  letter-spacing: -0.01em;
}
.fp__subtitle {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant, #564335);
  max-width: 300px;
  line-height: 1.6;
}

.fp__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: rgba(16, 181, 16, 0.08);
  color: #0a7a0a;
  font-size: 0.875rem;
  width: 100%;
  text-align: left;
}
[data-theme='dark'] .fp__success {
  background: rgba(34, 204, 34, 0.12);
  color: #4ade80;
}
.fp__success-icon {
  font-size: 1.1rem;
  color: #10b510;
}
[data-theme='dark'] .fp__success-icon {
  color: #4ade80;
}
.fp__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}
.fp__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container, #ffdad6);
  color: var(--nxt-on-error-container, #93000a);
  font-size: 0.875rem;
}
.fp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  text-align: left;
}
.fp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--nxt-on-surface-variant, #564335);
}
.fp__input {
  width: 100%;
  height: 52px;
  padding: 0 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(147, 130, 130, 0.2));
  background: rgba(255, 255, 255, 0.6);
  color: var(--nxt-on-surface, #191c1d);
  font-size: 0.9375rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
[data-theme='dark'] .fp__input {
  background: rgba(255, 255, 255, 0.05);
  color: var(--nxt-on-surface);
}
.fp__input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.18);
}
.fp__input::placeholder {
  color: var(--nxt-on-surface-muted, #938282);
}
[data-theme='dark'] .fp__icon-wrap {
  background: rgba(255, 214, 10, 0.1);
  border-color: rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .fp__icon {
  color: #ffd60a;
}
.fp__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  height: 54px;
  border-radius: 9999px;
  border: none;
  background: linear-gradient(135deg, #ffd60a 0%, #f5c500 100%);
  box-shadow:
    0 4px 20px rgba(255, 214, 10, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
  color: #0b1320;
  font-size: 1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  letter-spacing: -0.01em;
  transition:
    box-shadow 0.2s,
    transform 0.1s,
    opacity 0.15s;
}
.fp__btn:hover {
  box-shadow:
    0 6px 28px rgba(255, 214, 10, 0.55),
    0 1px 0 rgba(255, 255, 255, 0.4) inset;
}
.fp__btn:active {
  transform: scale(0.985);
}
.fp__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.fp__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
.fp__back {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(11, 19, 32, 0.45);
  text-decoration: none;
  transition: color 0.15s;
}
.fp__back:hover {
  color: #0b1320;
}
[data-theme='dark'] .fp__back {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .fp__back:hover {
  color: rgba(255, 255, 255, 0.7);
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
